import React, {useEffect, useMemo, useState} from 'react'
import {BoxProps, useMultiStyleConfig, VStack, Text, Divider, Link} from '@chakra-ui/react'
import {Checkbox, Select} from '_uiComponents'
import {Boutique, ProductVariant} from '_types'
import {FormattedMessage, useIntl} from 'react-intl'
import BoutiqueItem from './partials/boutique-item'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useQuery} from '@tanstack/react-query'
import {useCustomApi, useAnalytics} from '_customHooks'
import {useLoader} from '_context'

interface ProductFindInBoutiqueProps {
    productVariant: ProductVariant
    productName: string
    productId: string
}

/**
 * @name ProductFindInBoutique
 *
 * @component
 *
 * @description
 * Renders the find in boutique component.
 *
 *
 * @returns {React.FC} - The rendered find in boutique component.
 */
const ProductFindInBoutique: React.FC<ProductFindInBoutiqueProps> = ({productVariant, productName, productId}: ProductFindInBoutiqueProps) => {
    const intl = useIntl()
    const {formatMessage} = intl
    const {locale} = useMultiSite()
    const {useFindInBoutique} = useCustomApi()
    const {mutateAsync: findInBoutiqueMutation, isLoading: isLoadingBoutiques} = useFindInBoutique()
    const {showLoader, hideLoader} = useLoader()
    const {
        analyticsProductFindInBoutiqueBookAppointment,
        analyticsProductFindInBoutiqueCall,
        analyticsProductFindInBoutiqueCountryChange,
        analyticsProductFindInBoutiqueOpenLayer,
        analyticsProductFindInBoutiqueRedirect,
    } = useAnalytics()

    const currentCountryCode = locale?.id?.split('-')?.[1]
    const COUNTRY_LABEL = formatMessage({id: 'label.form.country', defaultMessage: 'Country'})

    const [boutiques, setBoutiques] = useState<Array<Boutique>>([])
    const [filterAvailableBoutiques, setFilterAvailableBoutiques] = useState<boolean>(false)
    const [selectedCountry, setSelectedCountry] = useState<string | undefined>(currentCountryCode)

    const filteredBoutiques = useMemo<Array<Boutique>>(
        () => (filterAvailableBoutiques ? boutiques.filter((b) => b.availability > 0) : boutiques),
        [boutiques, filterAvailableBoutiques],
    )

    const styles = useMultiStyleConfig('ProductFindInBoutique', {}) as Record<string, BoxProps>

    const getCountries = async () => {
        const baseUrl = `${window.location.protocol}//${window.location.host}`

        const response = await fetch(`${baseUrl}/pwaUtils-countries`, {
            method: 'GET',
        })

        if (!response.ok) {
            return []
        }

        const responseJson: Array<{country: string; defaultLocale: string}> = await response.json()
        return responseJson || []
    }

    const handleCountrySelection = (countryCode, sendAnalytics = true) => {
        showLoader()

        findInBoutiqueMutation({locale: locale.id, productId: productVariant.ID, countryCode})
            .then((response) => {
                if (response.boutiques?.length > 0) {
                    setBoutiques((prev) => response.boutiques)
                }
            })
            .catch(() => {})
            .finally(() => hideLoader())

        if (sendAnalytics) {
            analyticsProductFindInBoutiqueCountryChange({productId, selectedSizeLabel: productVariant.size.label})
        }
    }

    const {data: countries} = useQuery(['findInBoutiqueCountries'], getCountries)
    const sortedCountries = countries
        ?.map((country) => ({
            ...country,
            countryName: (intl.messages[`country.${country.country.toUpperCase()}`]?.[0]?.value as string) || country.country,
        }))
        .sort((a, b) => a.countryName.localeCompare(b.countryName))

    const getBookAppointmentLink = (boutique: Boutique): string => {
        const queryParams = new URLSearchParams({
            boutiqueId: boutique.ID,
            boutiqueCountry: selectedCountry ?? '',
        })
        return `/${locale.alias}/bookAppointment?${queryParams.toString()}`
    }

    useEffect(() => {
        handleCountrySelection(currentCountryCode, false)
        analyticsProductFindInBoutiqueOpenLayer({productId})
    }, [])

    return (
        <VStack {...styles.container}>
            <VStack {...styles.productInfoContainer}>
                <Text {...styles.productInfo}>
                    <FormattedMessage id="label.pdp.details.sku" defaultMessage={'SKU: {0}'} values={{0: productVariant?.ID}} />
                </Text>
                <Text {...styles.productInfo}>{productName}</Text>
                <Text {...styles.chooseCountrySubtitle}>
                    <FormattedMessage id="label.select.country.boutique" defaultMessage={'Select the country where you want to check product availability.'} />
                </Text>
            </VStack>

            <Select
                name="contry"
                label={COUNTRY_LABEL}
                isSearchable={true}
                options={
                    sortedCountries?.map((c, i) => ({
                        label: c.countryName,
                        value: c.country.toUpperCase(),
                        index: i,
                    })) || []
                }
                onOptionSelected={(selectedCountryCode) => {
                    setSelectedCountry(selectedCountryCode!)
                    handleCountrySelection(selectedCountryCode)
                }}
                value={selectedCountry}
            />

            <Text {...styles.infoText}>
                <FormattedMessage
                    id="label.message.customer.care.boutique"
                    defaultMessage={''}
                    values={{
                        link: (
                            <Link
                                textDecoration={'underline'}
                                textStyle={'paragraphSm'}
                                fontWeight={'normal'}
                                textUnderlineOffset={'8px'}
                                _hover={{textDecoration: 'underline !important'}}
                            >
                                <FormattedMessage id="label.message.customer.service" defaultMessage={''} />
                            </Link>
                        ),
                    }}
                />
            </Text>

            {/* No boutiques found info message */}
            {!isLoadingBoutiques && filteredBoutiques?.length === 0 && (
                <Text {...styles.infoText}>
                    <FormattedMessage id="label.message.no.boutique" defaultMessage={'Sorry, but there are no available boutiques in the selected country.'} />
                </Text>
            )}

            {/* Available boutiques filter */}
            {filteredBoutiques?.length > 0 && (
                <Checkbox
                    sx={styles.filterAvailableBoutiquesCheckbox}
                    onChange={(e) => {
                        setFilterAvailableBoutiques((_prev) => e.target?.checked)
                    }}
                >
                    <Text {...styles.filterAvailableBoutiques}>
                        <FormattedMessage id="label.checkbox.filter.availability" defaultMessage={'View only the Boutiques where the product is available'} />
                    </Text>
                </Checkbox>
            )}

            <VStack w={'100%'}>
                {filteredBoutiques?.map((boutique, index) => (
                    <React.Fragment key={`boutique-${index}`}>
                        <BoutiqueItem
                            name={boutique.name}
                            address={boutique.address}
                            opening={boutique.opening?.split('<br>')?.map((sub) => sub.trim())}
                            phone={boutique.phone}
                            availability={boutique.availability}
                            availabilityMsg={boutique.availabilityMsg}
                            bookAppointmentLink={getBookAppointmentLink(boutique)}
                            onCall={() =>
                                analyticsProductFindInBoutiqueCall({
                                    productId,
                                    selectedSizeLabel: productVariant.size.label,
                                    boutiqueName: boutique.name,
                                    boutiqueId: boutique.ID,
                                })
                            }
                            onBookAppointment={() => {
                                analyticsProductFindInBoutiqueBookAppointment({
                                    productId,
                                    selectedSizeLabel: productVariant.size.label,
                                    boutiqueName: boutique.name,
                                    boutiqueId: boutique.ID,
                                })
                                analyticsProductFindInBoutiqueRedirect({boutiqueName: boutique.name, boutiqueId: boutique.ID, redirectUrl: boutique.link})
                            }}
                        />
                        <Divider />
                    </React.Fragment>
                ))}
            </VStack>
        </VStack>
    )
}

export default ProductFindInBoutique
